import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { authService } from '../services';
import { Input, Button, Card, PasswordStrength } from '../components';
import './ResetPassword.css';

// Schema de validación con Yup
const resetPasswordSchema = yup.object().shape({
  newPassword: yup
    .string()
    .required('La contraseña es requerida')
    .min(10, 'La contraseña debe tener al menos 10 caracteres'),
  confirmPassword: yup
    .string()
    .required('Confirmar contraseña es requerido')
    .oneOf([yup.ref('newPassword')], 'Las contraseñas no coinciden'),
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  const newPasswordValue = watch('newPassword') || '';

  // Redirigir si no hay token
  React.useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  const onSubmit = async (data) => {
    setLoading(true);
    setApiError('');
    
    try {
      await authService.resetPassword(
        token,
        data.newPassword
      );
      
      // Mostrar mensaje de éxito
      setSuccess(true);
      
      // Redirigir a login después de 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setApiError(error.response?.data?.message || 'Error al resetear contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null; // Será redirigido por el useEffect
  }

  return (
    <div className="reset-password">
      <div className="reset-password__container">
        <div className="reset-password__header">
          <h1 className="reset-password__title">PICHTRAINING</h1>
          <p className="reset-password__subtitle">Crear Nueva Contraseña</p>
        </div>
        
        <Card>
          {success ? (
            <div className="reset-password__success">
              <div className="reset-password__success-icon">✓</div>
              <h2>Contraseña Actualizada</h2>
              <p>Tu contraseña ha sido actualizada exitosamente.</p>
              <p className="reset-password__redirect">Redirigiendo a login...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="reset-password__form">
              <p className="reset-password__description">
                Ingresa tu nueva contraseña para acceder a tu cuenta.
              </p>
              
              {apiError && (
                <div className="reset-password__error-banner">
                  {apiError}
                </div>
              )}
              
              <div className="reset-password__password-group">
                <Input
                  label="Nueva Contraseña"
                  type={showPasswords ? "text" : "password"}
                  placeholder="••••••••"
                  error={errors.newPassword?.message}
                  required
                  {...register('newPassword')}
                />
                <PasswordStrength password={newPasswordValue} />
              </div>
              
              <div className="reset-password__password-group">
                <Input
                  label="Confirmar Contraseña"
                  type={showPasswords ? "text" : "password"}
                  placeholder="••••••••"
                  error={errors.confirmPassword?.message}
                  required
                  {...register('confirmPassword')}
                />
              </div>
              
              <label className="reset-password__show-password">
                <input
                  type="checkbox"
                  checked={showPasswords}
                  onChange={() => setShowPasswords(!showPasswords)}
                />
                Mostrar contraseña
              </label>
              
              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={loading}
              >
                Actualizar Contraseña
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
